import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { Download, Loader2, Play } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { z } from "zod"
import {
	closeReconciliationPresentation,
	closeStatusPresentation,
	Money,
	QueryErrorLine,
	Segmented,
	StatusView,
} from "@/components/accounting/accounting-ui"
import { periodKey } from "@/components/accounting/reconciliation-format"
import { PageHeader, Panel, Toolbar, ToolbarSpacer } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	useBulkDownloadMonthlyCloses,
	useMonthlyCloses,
	useTriggerMonthlyCloseCron,
} from "@/queries/marketplace/use-monthly-closes"

const searchSchema = z.object({
	region: z.enum(["NA", "EU"]).optional(),
	currency: z.string().optional(),
	year: z.coerce.number().int().min(2000).max(3000).optional().catch(undefined),
	status: z.enum(["draft", "finalised"]).optional(),
})

export const Route = createFileRoute("/accounting/monthly-closes/")({
	component: MonthlyClosesPage,
	validateSearch: (search: Record<string, unknown>) =>
		searchSchema.parse(search),
})

function MonthlyClosesPage() {
	const search = useSearch({ from: "/accounting/monthly-closes/" })
	const navigate = useNavigate()
	const closes = useMonthlyCloses(search)
	const triggerCron = useTriggerMonthlyCloseCron()
	const bulkDownload = useBulkDownloadMonthlyCloses()

	// Bulk selection: closeId set. Cleared whenever the underlying list
	// changes (filters / refetch) so we don't carry stale ids forward.
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
	const items = closes.data?.items ?? []
	const allSelected =
		items.length > 0 && items.every((c) => selectedIds.has(c.closeId))
	const someSelected = items.some((c) => selectedIds.has(c.closeId))
	const toggleOne = (closeId: string) =>
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (next.has(closeId)) next.delete(closeId)
			else next.add(closeId)
			return next
		})
	const toggleAllVisible = () =>
		setSelectedIds((prev) => {
			if (allSelected) {
				const next = new Set(prev)
				for (const c of items) next.delete(c.closeId)
				return next
			}
			const next = new Set(prev)
			for (const c of items) next.add(c.closeId)
			return next
		})

	const handleBulkDownload = async () => {
		if (selectedIds.size === 0) return
		try {
			// Server bundles each selected close's workbook into a single
			// ZIP and returns a presigned URL: one navigation client-side,
			// no pop-up-blocker tripping like the previous sequential
			// `<a download>` approach.
			const result = await bulkDownload.mutateAsync(Array.from(selectedIds))
			const a = document.createElement("a")
			a.href = result.downloadUrl
			a.download = result.filename
			a.rel = "noreferrer"
			document.body.appendChild(a)
			a.click()
			a.remove()
			if (result.missingCount > 0) {
				toast.success(
					`Downloading ${result.includedCount} workbook${result.includedCount === 1 ? "" : "s"}. ${result.missingCount} weren't ready yet and were skipped.`,
				)
			} else {
				toast.success(
					`Downloading ${result.includedCount} workbook${result.includedCount === 1 ? "" : "s"}.`,
				)
			}
		} catch (e) {
			const code = (e as { code?: string }).code
			if (code === "NO_WORKBOOKS_AVAILABLE") {
				toast.error("None of the selected closes have a workbook available.")
				return
			}
			toast.error(
				e instanceof Error
					? `Bulk download failed: ${e.message}`
					: "Bulk download failed.",
			)
		}
	}

	const updateFilter = (key: string, value: string | undefined) => {
		navigate({
			to: "/accounting/monthly-closes",
			search: (prev) => {
				const next = { ...prev, [key]: value }
				if (!value || value === "all")
					delete (next as Record<string, unknown>)[key]
				return next as never
			},
			replace: true,
		})
	}

	const runCron = async () => {
		try {
			await triggerCron.mutateAsync()
			toast.success(
				"Cron tick enqueued. Drafts will appear as the worker processes each region.",
			)
		} catch {
			toast.error("Could not enqueue cron tick.")
		}
	}

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Monthly closes"
				count={closes.data ? items.length : undefined}
				description="Per-region, per-month accounting closes, with each currency nested inside its close. Finalise to lock numbers."
				actions={
					<Button
						variant="outline"
						onClick={runCron}
						disabled={triggerCron.isPending}
						title="The nightly tick regenerates the current and previous month; older drafts regenerate on request."
					>
						{triggerCron.isPending ? (
							<Loader2 className="animate-spin" />
						) : (
							<Play />
						)}
						Run cron now
					</Button>
				}
			/>

			<Toolbar>
				<Select
					value={search.region ?? "all"}
					onValueChange={(v) =>
						updateFilter("region", v === "all" ? undefined : v)
					}
				>
					<SelectTrigger aria-label="Region" className="w-[140px]">
						<SelectValue placeholder="Region" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All regions</SelectItem>
						<SelectItem value="NA">NA</SelectItem>
						<SelectItem value="EU">EU</SelectItem>
					</SelectContent>
				</Select>
				<Segmented
					ariaLabel="Status"
					value={search.status ?? "all"}
					onChange={(v) => updateFilter("status", v === "all" ? undefined : v)}
					options={[
						{ value: "all", label: "All" },
						{ value: "draft", label: "Draft" },
						{ value: "finalised", label: "Finalised" },
					]}
				/>
				<ToolbarSpacer />
				{selectedIds.size > 0 && (
					<>
						<span className="text-xs text-meta tabular-nums">
							{selectedIds.size} selected
						</span>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setSelectedIds(new Set())}
						>
							Clear
						</Button>
						<Button
							size="sm"
							onClick={handleBulkDownload}
							disabled={bulkDownload.isPending}
						>
							{bulkDownload.isPending ? (
								<Loader2 className="animate-spin" />
							) : (
								<Download />
							)}
							Download {selectedIds.size} workbook
							{selectedIds.size === 1 ? "" : "s"} (zip)
						</Button>
					</>
				)}
			</Toolbar>

			{closes.isError ? (
				<QueryErrorLine what="closes" error={closes.error} />
			) : (
				<Panel>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-10">
									<Checkbox
										checked={
											allSelected
												? true
												: someSelected
													? "indeterminate"
													: false
										}
										onCheckedChange={toggleAllVisible}
										aria-label="Select all visible closes"
										disabled={items.length === 0}
									/>
								</TableHead>
								<TableHead>Period</TableHead>
								<TableHead>Region</TableHead>
								<TableHead>Status</TableHead>
								<TableHead title="Ledger + VAT + internal tie-outs, evaluated nightly for the previous month">
									Reconciliation
								</TableHead>
								<TableHead title="Latest complete version and its generation state">
									Version
								</TableHead>
								<TableHead
									className="text-right"
									title="Gross GMV − Refunded GMV (in-period). Cancelled orders aren't in Gross."
								>
									Net GMV (EUR)
								</TableHead>
								<TableHead
									className="text-right"
									title="Recognised GMV: charged totals of the earned closures whose first terminal transition falls in the month (R1)."
								>
									Recognised GMV (EUR)
								</TableHead>
								<TableHead className="text-right">Net revenue (EUR)</TableHead>
								<TableHead
									className="text-right"
									title="Retained buyer-paid shipping − carrier label cost, on managed (SendCloud) orders. 0 for seller-shipped."
								>
									Shipping margin (EUR)
								</TableHead>
								<TableHead className="text-right">
									Closing balance (EUR)
								</TableHead>
								<TableHead className="text-right">Overdue payouts</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{closes.isLoading &&
								[0, 1, 2, 3, 4].map((row) => (
									<TableRow key={row} className="h-9">
										<TableCell colSpan={12}>
											<Skeleton className="h-3.5 w-full" />
										</TableCell>
									</TableRow>
								))}
							{!closes.isLoading && items.length === 0 && (
								<TableRow className="hover:bg-transparent">
									<TableCell colSpan={12} className="text-muted-foreground">
										No closes yet. The cron generates one daily.
									</TableCell>
								</TableRow>
							)}
							{items.map((c) => (
								<TableRow
									key={c.closeId}
									onClick={(e) => {
										const target = e.target as HTMLElement
										if (target.closest("[data-row-no-navigate]")) return
										navigate({
											to: "/accounting/monthly-closes/$closeId",
											params: { closeId: c.closeId },
										})
									}}
									className="cursor-pointer"
									data-state={
										selectedIds.has(c.closeId) ? "selected" : undefined
									}
								>
									<TableCell data-row-no-navigate>
										<Checkbox
											checked={selectedIds.has(c.closeId)}
											onCheckedChange={() => toggleOne(c.closeId)}
											aria-label={`Select close ${periodKey(c.year, c.month)} ${c.region}`}
										/>
									</TableCell>
									<TableCell className="font-mono">
										{periodKey(c.year, c.month)}
									</TableCell>
									<TableCell>{c.region}</TableCell>
									<TableCell>
										<StatusView
											presentation={closeStatusPresentation(c.status)}
										/>
									</TableCell>
									<TableCell>
										<StatusView
											presentation={closeReconciliationPresentation(
												c.reconciliationStatus,
											)}
										/>
									</TableCell>
									<TableCell>
										<span className="flex items-center gap-2">
											<span className="font-mono">v{c.version}</span>
											{c.generationStatus === "queued" ||
											c.generationStatus === "running" ? (
												<Loader2 className="size-3 animate-spin text-meta" />
											) : c.generationStatus === "failed" ? (
												<StatusTag tone="bad">Failed</StatusTag>
											) : c.inputsChanged ? (
												<StatusTag tone="warn">Inputs changed</StatusTag>
											) : null}
										</span>
									</TableCell>
									<TableCell className="text-right font-medium">
										<Money cents={c.netGmvEur} currency="EUR" />
									</TableCell>
									<TableCell className="text-right text-muted-foreground">
										<Money cents={c.gmvEur} currency="EUR" />
									</TableCell>
									<TableCell className="text-right">
										<Money cents={c.netPlatformRevenueEur} currency="EUR" />
									</TableCell>
									<TableCell className="text-right">
										<Money cents={c.shippingMarginEur} currency="EUR" />
									</TableCell>
									<TableCell className="text-right">
										<Money cents={c.closingBalanceEur} currency="EUR" />
									</TableCell>
									<TableCell className="text-right">
										{c.stuckLiabilityCount > 0 ? (
											<StatusTag tone="bad" className="tabular-nums">
												{c.stuckLiabilityCount} (
												<Money
													cents={c.stuckLiabilityAmountEur}
													currency="EUR"
												/>
												)
											</StatusTag>
										) : (
											<span className="text-meta">–</span>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Panel>
			)}
		</div>
	)
}
